import { supabase } from "./supabaseClient.js";

export async function getCategories() {
  const { data, error } = await supabase
    .from('categories')
    .select('id:id_categories,name')
    .order('name');
  if (error) return [];
  return data || [];
}

export async function getSuppliers() {
  const { data, error } = await supabase
    .from('suppliers')
    .select('id:id_suppliers,name')
    .order('name');
  if (error) return [];
  return data || [];
}

export async function getTaxes() {
  const { data, error } = await supabase
    .from('taxes')
    .select('id:id_taxes,name,rate')
    .order('name');
  if (error) return [];
  return data || [];
}

// Asegura que exista un impuesto con tasa dada y devuelve su id
export async function ensureTax(rate, name = 'IVA 16%') {
  const r = Number(rate);
  // 1) Buscar por tasa existente
  const { data: byRate } = await supabase
    .from('taxes')
    .select('id:id_taxes, name, rate')
    .eq('rate', r)
    .limit(1);
  if (byRate && byRate.length) return byRate[0].id;
  // 2) Crear/actualizar por nombre único usando upsert
  const displayName = String(name);
  await supabase
    .from('taxes')
    .upsert({ name: displayName, rate: r }, { onConflict: 'name' });
  const { data: byName } = await supabase
    .from('taxes')
    .select('id:id_taxes')
    .eq('name', displayName)
    .limit(1);
  return (byName && byName.length) ? byName[0].id : null;
}

export async function addSupplierIfNeeded(name) {
  if (!name) return null;
  const { data: existing } = await supabase
    .from('suppliers')
    .select('id:id_suppliers')
    .eq('name', name)
    .limit(1);
  if (existing && existing.length) return existing[0].id;
  const { data } = await supabase
    .from('suppliers')
    .insert({ name })
    .select('id_suppliers')
    .single();
  return data?.id_suppliers ?? null;
}

export async function addCategoryIfNeeded(name) {
  if (!name) return null;
  console.log('🔍 addCategoryIfNeeded llamado con:', name);
  
  try {
    // Verificar si ya existe
    const { data: existing, error: existingError } = await supabase
      .from('categories')
      .select('id:id_categories')
      .eq('name', name)
      .limit(1);
    
    console.log('📋 Verificación de categoría existente:', { existing, existingError });
    
    if (existingError) {
      console.error('❌ Error al verificar categoría existente:', existingError);
      return null;
    }
    
    if (existing && existing.length) {
      console.log('✅ Categoría existente encontrada:', existing[0].id);
      return existing[0].id;
    }
    
    // Crear nueva categoría
    console.log('📝 Creando nueva categoría:', name);
    const { data, error } = await supabase
      .from('categories')
      .insert({ name })
      .select('id_categories')
      .single();
    
    console.log('📊 Resultado de inserción:', { data, error });
    
    if (error) {
      console.error('❌ Error al crear categoría:', error);
      return null;
    }
    
    const categoryId = data?.id_categories ?? null;
    console.log('✅ Nueva categoría creada con ID:', categoryId);
    return categoryId;
    
  } catch (err) {
    console.error('❌ Error en addCategoryIfNeeded:', err);
    return null;
  }
}

export async function addProductWithInitialStock({
  sku,
  barcode,
  name,
  category_id,
  supplier_id,
  cost_price,
  unit_price,
  tax_id,
  reorder_level,
  initial_qty,
  notes,
  image_base64,
  image_mime,
  discount,
  description,
}) {
  const { data: prod } = await supabase
    .from('products')
    .insert({
      sku: sku ?? null,
      barcode: barcode ?? null,
      name,
      category_id: category_id ?? null,
      supplier_id: supplier_id ?? null,
      cost_price: Number(cost_price ?? 0),
      unit_price: Number(unit_price ?? 0),
      tax_id: tax_id ?? null,
      reorder_level: Number(reorder_level ?? 0),
      discount: Number(discount ?? 0),
      description: description ?? null,
      notes: notes ?? null,
    })
    .select('id_products')
    .single();
  const productId = prod?.id_products;
  if (initial_qty && Number(initial_qty) > 0 && productId) {
    await supabase.from('stock_movements').insert({
      product_id: productId,
      kind: 'initial',
      quantity: Number(initial_qty),
      unit_value: Number(cost_price ?? 0),
      notes: notes ?? 'Inv. inicial',
    });
  }
  if (image_base64 && image_base64.length && productId) {
    await saveProductImage(productId, image_base64, image_mime || 'image/jpeg');
  }
  return productId;
}

export async function listInventory() {
  const { data, error } = await supabase
    .from('v_inventory_basic')
    .select('*')
    .order('name');
  if (error) return [];
  const rows = data || [];
  const ids = rows.map((r) => r.id).filter((v) => v != null);
  let mapBarcode = new Map();
  if (ids.length) {
    const { data: barRows } = await supabase
      .from('products')
      .select('id_products, barcode')
      .in('id_products', ids);
    (barRows || []).forEach((b) => {
      mapBarcode.set(b.id_products, b.barcode || null);
    });
  }
  return rows.map(r => ({
    id: r.id,
    sku: r.sku,
    barcode: mapBarcode.get(r.id) ?? r.barcode ?? null,
    name: r.name,
    unit_price: r.unit_price,
    cost_price: r.cost_price,
    category: r.category,
    supplier: r.supplier,
    category_id: r.category_id,
    supplier_id: r.supplier_id,
    tax_id: r.tax_id,
    tax_rate: r.tax_rate,
    stock_qty: r.stock_qty,
    reorder_level: r.reorder_level,
    discount: r.discount,
    description: r.description,
    notes: r.notes,
    created_at: r.created_at,
    image_base64: r.image_base64,
    image_mime: r.image_mime,
  }));
}

export async function updateProductBasic({
  id,
  sku,
  barcode,
  name,
  unit_price,
  cost_price,
  reorder_level,
  discount,
  description,
  notes,
  category_id,
  supplier_id,
  tax_id,
}) {
  // Construir UPDATE dinámico: sólo modifica columnas proporcionadas
  const fields = {};
  if (sku !== undefined) fields.sku = sku ?? null;
  if (barcode !== undefined) fields.barcode = barcode ?? null;
  if (name !== undefined) fields.name = name;
  if (unit_price !== undefined) fields.unit_price = Number(unit_price);
  if (cost_price !== undefined) fields.cost_price = Number(cost_price);
  if (reorder_level !== undefined) fields.reorder_level = Number(reorder_level || 0);
  if (discount !== undefined) fields.discount = Number(discount || 0);
  if (description !== undefined) fields.description = description ?? null;
  if (notes !== undefined) fields.notes = notes ?? null;
  if (category_id !== undefined) fields.category_id = category_id ?? null;
  if (supplier_id !== undefined) fields.supplier_id = supplier_id ?? null;
  if (tax_id !== undefined) fields.tax_id = tax_id ?? null;
  if (!Object.keys(fields).length) return { ok: true };
  const { error } = await supabase
    .from('products')
    .update(fields)
    .eq('id_products', id);
  return { ok: !error };
}

export async function softDeleteProduct(id) {
  const { error } = await supabase
    .from('products')
    .update({ active: false })
    .eq('id_products', id);
  return { ok: !error };
}

export async function saveProductImage(
  product_id,
  image_base64,
  mime = "image/jpeg"
) {
  const { error } = await supabase
    .from('product_images')
    .insert({ product_id, image_base64, mime });
  return { ok: !error };
}

export async function getProductImage(product_id) {
  const { data } = await supabase
    .from('product_images')
    .select('image_base64,mime')
    .eq('product_id', product_id)
    .order('created_at', { ascending: false })
    .limit(1);
  return (data && data.length) ? data[0] : null;
}

export async function getProductImages(product_id, limit = 6) {
  const { data } = await supabase
    .from('product_images')
    .select('image_base64,mime')
    .eq('product_id', product_id)
    .order('created_at', { ascending: false })
    .limit(Number(limit));
  return data || [];
}

export async function getProductDetails(product_id) {
  let base;
  const { data: baseRows } = await supabase
    .from('v_product_base')
    .select('*')
    .eq('id', product_id)
    .limit(1);
  base = (baseRows && baseRows[0]) || null;
  if (!base) return null;
  const [
    images,
    variants,
    portions,
    ingredients,
    nutritionRows,
    prepRows,
    reviews,
  ] = await Promise.all([
    getProductImages(product_id, 6),
    (async () => {
      const { data } = await supabase
        .from('product_variants')
        .select('id:id_product_variants,name,price_delta,available')
        .eq('product_id', product_id)
        .order('name');
      return data || [];
    })(),
    (async () => {
      const { data } = await supabase
        .from('product_portions')
        .select('id:id_product_portions,name,multiplier')
        .eq('product_id', product_id)
        .order('multiplier');
      return data || [];
    })(),
    (async () => {
      const { data } = await supabase
        .from('product_ingredients')
        .select('name,is_allergen')
        .eq('product_id', product_id)
        .order('name');
      return data || [];
    })(),
    (async () => {
      const { data } = await supabase
        .from('product_nutrition')
        .select('calories,protein,carbs,fat')
        .eq('product_id', product_id)
        .limit(1);
      return data || [];
    })(),
    (async () => {
      const { data } = await supabase
        .from('product_preparation')
        .select('prepared_in_kitchen,delivery_local,origin,eta_min_minutes,eta_max_minutes')
        .eq('product_id', product_id)
        .limit(1);
      return data || [];
    })(),
    (async () => {
      const { data } = await supabase
        .from('product_reviews')
        .select('author,rating,comment,created_at')
        .eq('product_id', product_id)
        .order('created_at', { ascending: false })
        .limit(10);
      return data || [];
    })(),
  ]);
  const nutrition = nutritionRows[0] || null;
  const preparation = prepRows[0] || null;
  return {
    ...base,
    images,
    variants,
    portions,
    ingredients,
    nutrition,
    preparation,
    reviews,
  };
}

// Aplica un ajuste de stock (positivo suma, negativo resta)
export async function applyStockAdjustment({ product_id, quantity, unit_value, notes }) {
  const delta = Number(quantity || 0);
  if (!product_id || delta === 0) return { ok: true };
  const absQty = Math.abs(delta);
  const kind = delta > 0 ? 'adjustment' : 'sale'; // resta usa 'sale' para cumplir CHECK (qty >= 0)
  const { error } = await supabase
    .from('stock_movements')
    .insert({
      product_id,
      kind,
      quantity: absQty,
      unit_value: Number(unit_value || 0),
      notes: notes ?? (delta > 0 ? 'Ajuste +stock' : 'Ajuste -stock'),
    });
  return { ok: !error };
}
